import { filterByDietary } from './dietary';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MEALS = ['breakfast', 'lunch', 'dinner'];

/** Compute per-recipe scores for a group of family members. */
function getScores(recipes, members, swipes, feedback) {
  const scores = {};
  recipes.forEach(r => { scores[r.id] = 1; });

  // Swipe weight
  members.forEach(m => {
    const memberSwipes = swipes[m.id] || {};
    Object.entries(memberSwipes).forEach(([rid, liked]) => {
      scores[rid] = (scores[rid] || 1) + (liked ? 3 : -2);
    });
  });

  // Feedback weight (star ratings from past meals)
  const memberIds = new Set(members.map(m => m.id));
  (feedback || []).forEach(f => {
    if (!memberIds.has(f.member_id)) return;
    if (f.stars >= 4) scores[f.recipe_id] = (scores[f.recipe_id] || 1) + 2;
    else if (f.stars <= 2) scores[f.recipe_id] = (scores[f.recipe_id] || 1) - 1;
  });

  return scores;
}

/** Weighted-random pick from a pool, avoiding `used` IDs. */
function pickWeighted(pool, scores, used) {
  const available = pool.filter(r => !used.has(r.id) && (scores[r.id] || 1) > 0);
  if (!available.length) return pool[Math.floor(Math.random() * pool.length)];
  const total = available.reduce((s, r) => s + Math.max(scores[r.id] || 1, 0.5), 0);
  let rand = Math.random() * total;
  for (const r of available) {
    rand -= Math.max(scores[r.id] || 1, 0.5);
    if (rand <= 0) return r;
  }
  return available[available.length - 1];
}

/** Collect union of dietary restrictions for a group of members. */
function getGroupRestrictions(members) {
  const all = new Set();
  members.forEach(m => (m.allergies || []).forEach(a => all.add(a)));
  return [...all];
}

/**
 * Generate a weighted weekly meal plan.
 *
 * @param {Array} recipes     - Full recipe list from Supabase
 * @param {Object} swipes     - { memberId: { recipeId: true/false } }
 * @param {Array} feedback    - Array of { recipe_id, member_id, stars } from meal_feedback
 * @param {Array} family      - Array of { id, type: 'adult'|'kid' }
 * @param {Array} plannedMeals - Which meal categories to fill (default: all).
 *                               Only these appear in the plan (e.g. ['dinner']).
 * @returns {Object}          - { Mon: { dinner: {...}, ... }, ... }
 */
export function generateWeightedPlan(recipes, swipes, feedback, family, plannedMeals = MEALS) {
  const adults = family.filter(m => m.type === 'adult');
  const kids = family.filter(m => m.type === 'kid');

  const adultScores = getScores(recipes, adults, swipes, feedback);
  const kidScores = getScores(recipes, kids, swipes, feedback);

  const allRestrictions = getGroupRestrictions(family);

  // Group recipes by category
  const byCategory = {};
  recipes.forEach(r => {
    if (!byCategory[r.category]) byCategory[r.category] = [];
    byCategory[r.category].push(r);
  });

  // Combined scores for shared meals
  const combined = {};
  recipes.forEach(r => {
    combined[r.id] = Math.max((adultScores[r.id] || 1) + (kidScores[r.id] || 1), 0.5);
  });

  // Fill only the household's selected meals, kept in canonical order.
  const enabled = plannedMeals?.length ? plannedMeals : MEALS;
  const meals = MEALS.filter(m => enabled.includes(m));

  const plan = {};

  DAYS.forEach(day => {
    const dayPlan = {};
    meals.forEach(meal => {
      const pool = byCategory[meal] || recipes;
      const sharedPool = filterByDietary(pool, allRestrictions);
      const pick = pickWeighted(sharedPool.length ? sharedPool : pool, combined, new Set());
      dayPlan[meal] = { shared: true, recipeId: pick.id };
    });
    plan[day] = dayPlan;
  });

  flagMysteryDinner(plan, combined);
  return plan;
}

/**
 * Flag one shared dinner as the week's "Mystery Meal" — the highest-scoring
 * dinner, so the surprise is likely to be a hit. Mutates and returns `plan`.
 * Deterministic (ties resolve to the earliest day) for testability.
 */
export function flagMysteryDinner(plan, combined) {
  let best = null;
  for (const day of DAYS) {
    const slot = plan[day]?.dinner;
    if (!slot?.shared || !slot.recipeId) continue;
    const score = combined[slot.recipeId] || 0;
    if (!best || score > best.score) best = { day, score };
  }
  if (best) plan[best.day].dinner.mystery = true;
  return plan;
}

/**
 * Pick a single replacement meal for one slot in an existing plan.
 *
 * @param {Array}  recipes     - Full recipe list
 * @param {Object} swipes      - { memberId: { recipeId: true/false } }
 * @param {Array}  feedback    - Array of { recipe_id, member_id, stars }
 * @param {Array}  family      - Array of { id, type: 'adult'|'kid' }
 * @param {Object} currentPlan - The plan object (Mon..Sun → meals)
 * @param {string} day         - e.g. 'Mon'
 * @param {string} meal        - 'breakfast' | 'lunch' | 'dinner'
 * @param {string} variant     - 'shared' | 'adult' | 'kid'
 * @returns {Object}           - New slot object for plan[day][meal]
 */
export function pickSingleMeal(recipes, swipes, feedback, family, currentPlan, day, meal, variant) {
  const adults = family.filter(m => m.type === 'adult');
  const kids = family.filter(m => m.type === 'kid');

  const adultScores = getScores(recipes, adults, swipes, feedback);
  const kidScores = getScores(recipes, kids, swipes, feedback);

  const adultRestrictions = getGroupRestrictions(adults);
  const kidRestrictions = getGroupRestrictions(kids);
  const allRestrictions = getGroupRestrictions(family);

  // Group recipes by category
  const byCategory = {};
  recipes.forEach(r => {
    if (!byCategory[r.category]) byCategory[r.category] = [];
    byCategory[r.category].push(r);
  });

  const pool = byCategory[meal] || recipes;

  // Build dedup set: all recipe IDs used for this meal category across the week,
  // excluding the current slot being re-rolled.
  const used = new Set();
  DAYS.forEach(d => {
    const slot = currentPlan[d]?.[meal];
    if (!slot) return;
    const isCurrentSlot = d === day;
    if (slot.shared) {
      if (!isCurrentSlot) used.add(slot.recipeId);
    } else {
      // For split meals, only exclude the variant NOT being re-rolled,
      // plus both variants from other days
      if (isCurrentSlot) {
        // Keep the other variant in the dedup set so we don't accidentally
        // pick the same recipe and unintentionally merge a split meal
        if (variant === 'adult') used.add(slot.kidRecipeId);
        else if (variant === 'kid') used.add(slot.adultRecipeId);
      } else {
        used.add(slot.adultRecipeId);
        used.add(slot.kidRecipeId);
      }
    }
  });

  if (variant === 'shared') {
    const sharedPool = filterByDietary(pool, allRestrictions);
    const combined = {};
    recipes.forEach(r => {
      combined[r.id] = Math.max((adultScores[r.id] || 1) + (kidScores[r.id] || 1), 0.5);
    });
    const pick = pickWeighted(sharedPool.length ? sharedPool : pool, combined, used);
    return { shared: true, recipeId: pick.id };
  }

  if (variant === 'adult') {
    const adultPool = filterByDietary(pool, adultRestrictions);
    const pick = pickWeighted(adultPool.length ? adultPool : pool, adultScores, used);
    const currentSlot = currentPlan[day][meal];
    return { shared: false, adultRecipeId: pick.id, kidRecipeId: currentSlot.kidRecipeId };
  }

  // Kid variant
  const kidPool = filterByDietary(pool, kidRestrictions);
  const pick = pickWeighted(kidPool.length ? kidPool : pool, kidScores, used);
  const currentSlot = currentPlan[day][meal];
  return { shared: false, adultRecipeId: currentSlot.adultRecipeId, kidRecipeId: pick.id };
}

/**
 * Get current Monday as YYYY-MM-DD for the week_of field.
 *
 * Computed entirely in UTC so the day-of-week arithmetic and the
 * toISOString() serialization agree. The old version mixed local
 * getDay/getDate with a UTC toISOString(), which could return a non-Monday
 * (off by a day) in timezones behind UTC.
 */
export function getCurrentWeekOf() {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  const day = d.getUTCDay(); // 0=Sun … 6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

export { DAYS, MEALS };
